import { useEffect, useState } from "react";
import type { PublicEvent } from "@/services/api/publicEvents";
import { formatEventDate } from "../shared/dateTime";
import { calculateCountdown } from "../shared/countdown";

// JUB-94: countdown arithmetic now lives in ../shared/countdown so
// golden_serenity and botez_bokeh (and the golden_serenity template-engine
// package, via context.ts) can never drift out of sync again.
export { calculateCountdown };

/**
 * Golden Serenity local accent constants (template-specific, from the Figma
 * "Design 11 Jubiel Golden Serenity" prototype). Primary colors (background,
 * text, muted text, gold accent) are consumed via the config prop; these are
 * the secondary botanical accents unique to this design.
 */
export const GS = {
  gold: "#C9A86A",
  goldWarm: "#D4A574",
  sage: "#A4B494",
  sageDeep: "#8B9A7A",
  border: "#E8DCC8",
  frame: "#D4C4AB",
  creamLight: "#FDF8F3",
  cream: "#FAF6F0",
  creamDeep: "#F5EFE7",
  // JUB-90: label color for gold-background CTAs. `creamLight` text on
  // `gold` background is 2.1:1 (fails WCAG AA 4.5:1) — this deep espresso
  // brown keeps the same warm palette and gives ~5.7:1.
  ink: "#3D2F1F",
} as const;

// Check if RSVP cutoff date has passed
export const isRsvpCutoffPassed = (event: { rsvpCutoffDate?: string | null }): boolean => {
  if (!event.rsvpCutoffDate) return false;
  return new Date() > new Date(event.rsvpCutoffDate);
};

// JUB-225: starting 1 day before the event date, promote the Gift CTA in the hero when gifts are enabled
export const isEventEveOrDay = (event: { date?: string | null }): boolean => {
  if (!event.date) return false;
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return false;
  const eveDate = new Date(eventDate);
  eveDate.setDate(eveDate.getDate() - 1);
  eveDate.setHours(0, 0, 0, 0);
  return new Date() >= eveDate;
};

// Registry visibility gate (same logic as the other templates)
export const shouldShowGiftRegistry = (event: PublicEvent): boolean =>
  Boolean(event.registryPublic && event.giftsActive && (event as unknown as Record<string, boolean>).hasEnabledGifts);

// Format date helper — JUB-127: renders the event/ceremony wall clock verbatim,
// never shifted by the viewer's browser timezone (see shared/dateTime.ts).
export const formatDate = (
  value: string | undefined | null,
  locale: string,
  options: Intl.DateTimeFormatOptions,
) => formatEventDate(value, locale, options);

/** True when the visitor prefers reduced motion (shimmer/float/ticking pauses). */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
}
